import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, Modal, Select, Spinner, Textarea } from '../../components/ui'
import { clp, formatDateShort, QUOTE_STATUS } from '../../utils/format'
import type { Company, Quote } from '../../types/api'
import { Icon } from '../../components/ui/icons'

/**
 * Gestión de cotizaciones B2B desde el panel: responder con precios y
 * vigencia, formalizar cotizaciones del cotizador público (vincular
 * empresa) y convertir las aceptadas en convenio.
 */
export function QuotesAdminPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const canRespond = user != null && ['ADMIN', 'ACCOUNTANT'].includes(user.role)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('')
  const [detail, setDetail] = useState<Quote | null>(null)
  const [prices, setPrices] = useState<Record<string, string>>({})
  const [validUntil, setValidUntil] = useState('')
  const [responseNotes, setResponseNotes] = useState('')
  const [assignCompanyId, setAssignCompanyId] = useState('')

  const quotes = useQuery({
    queryKey: ['quotes', filter],
    queryFn: async () => (await api.get<Quote[]>('/quotes', { params: filter ? { status: filter } : {} })).data,
  })
  const companies = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await api.get<Company[]>('/companies')).data,
  })

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['quotes'] })
    queryClient.invalidateQueries({ queryKey: ['companies'] })
  }

  function openDetail(q: Quote) {
    setDetail(q)
    setPrices(Object.fromEntries(q.items.map((i) => [i.id, i.unitPrice != null ? String(i.unitPrice) : ''])))
    setValidUntil(q.validUntil ? q.validUntil.slice(0, 10) : '')
    setResponseNotes(q.responseNotes ?? '')
    setAssignCompanyId('')
    setError('')
  }

  const respond = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/quotes/${detail!.id}/respond`, {
          items: detail!.items.map((i) => ({ id: i.id, unitPrice: Number(prices[i.id]) })),
          validUntil: validUntil || null,
          responseNotes: responseNotes || null,
        })
      ).data,
    onSuccess: (q: Quote) => {
      setDetail(q)
      setError('')
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const assign = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${detail!.id}/assign-company`, { companyId: assignCompanyId })).data,
    onSuccess: (q: Quote) => {
      openDetail(q)
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const convert = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${detail!.id}/convert-to-agreement`)).data,
    onSuccess: (q: Quote) => {
      setDetail(q)
      setError('')
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const cancel = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${detail!.id}/cancel`, {})).data,
    onSuccess: () => {
      setDetail(null)
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const allPriced = detail?.items.every((i) => Number(prices[i.id]) > 0) ?? false

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-ink">Cotizaciones</h1>
        <div className="w-44">
          <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">Todas</option>
            {Object.entries(QUOTE_STATUS).map(([v, s]) => (
              <option key={v} value={v}>{s.label}</option>
            ))}
          </Select>
        </div>
      </div>

      <Card>
        {quotes.isLoading ? (
          <Spinner />
        ) : (quotes.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin cotizaciones para el filtro." />
        ) : (
          <div className="space-y-2">
            {(quotes.data ?? []).map((q) => (
              <button
                key={q.id}
                onClick={() => openDetail(q)}
                className="block w-full rounded-lg border border-gray-200 p-3 text-left transition-colors hover:border-brand-500"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-mono text-xs text-gray-500">{q.codeLabel}</span>{' '}
                    <span className="text-sm font-semibold text-ink">
                      {q.company?.businessName ?? q.companyName ?? q.contactName}
                    </span>
                    {!q.company && <Badge className="ml-1.5 bg-violet-100 text-violet-700">Web pública</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    {q.total != null && <span className="text-sm font-semibold text-ink">{clp(q.total)}</span>}
                    <Badge className={QUOTE_STATUS[q.status].className}>{QUOTE_STATUS[q.status].label}</Badge>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {q.items.map((i) => `${i.quantity}× ${i.battery.name}`).join(' · ')} · {formatDateShort(q.createdAt)} · {q.contactEmail}
                </p>
              </button>
            ))}
          </div>
        )}
      </Card>

      {detail && (
        <Modal title={`Cotización ${detail.codeLabel}`} onClose={() => setDetail(null)} wide>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge className={QUOTE_STATUS[detail.status].className}>{QUOTE_STATUS[detail.status].label}</Badge>
              <span className="text-gray-600">
                {detail.contactName} · {detail.contactEmail}
                {detail.contactPhone ? ` · ${detail.contactPhone}` : ''}
              </span>
            </div>
            {detail.company ? (
              <p className="rounded-lg bg-soft px-3 py-2 text-sm">
                Empresa: <strong>{detail.company.businessName}</strong> ({detail.company.rut})
              </p>
            ) : (
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
                <p className="text-sm text-violet-800">
                  Cotización del cotizador público para <strong>{detail.companyName ?? 'empresa sin nombre'}</strong>. Para responderla,
                  primero crea la empresa en Mantenedores y vincúlala aquí:
                </p>
                {canRespond && (
                  <div className="mt-2 flex items-end gap-2">
                    <div className="flex-1">
                      <Select value={assignCompanyId} onChange={(e) => setAssignCompanyId(e.target.value)}>
                        <option value="">Selecciona empresa…</option>
                        {(companies.data ?? []).map((c) => (
                          <option key={c.id} value={c.id}>{c.businessName}</option>
                        ))}
                      </Select>
                    </div>
                    <Button disabled={!assignCompanyId || assign.isPending} onClick={() => assign.mutate()}>Vincular</Button>
                  </div>
                )}
              </div>
            )}
            {detail.message && (
              <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
                <span className="font-medium">Mensaje:</span> {detail.message}
              </p>
            )}

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                  <th className="py-1.5">Batería</th>
                  <th className="py-1.5">Cantidad</th>
                  <th className="py-1.5">Precio unitario CLP</th>
                  <th className="py-1.5 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {detail.items.map((i) => (
                  <tr key={i.id}>
                    <td className="py-2">{i.battery.name}</td>
                    <td className="py-2">{i.quantity}</td>
                    <td className="py-2">
                      {canRespond && ['REQUESTED', 'RESPONDED'].includes(detail.status) ? (
                        <Input
                          type="number"
                          className="max-w-36"
                          value={prices[i.id] ?? ''}
                          onChange={(e) => setPrices({ ...prices, [i.id]: e.target.value })}
                        />
                      ) : i.unitPrice != null ? (
                        clp(i.unitPrice)
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 text-right">
                      {Number(prices[i.id]) > 0 ? clp(Number(prices[i.id]) * i.quantity) : i.unitPrice != null ? clp(i.unitPrice * i.quantity) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {canRespond && ['REQUESTED', 'RESPONDED'].includes(detail.status) && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Válida hasta">
                  <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                </Field>
                <Field label="Condiciones / notas">
                  <Textarea rows={2} value={responseNotes} onChange={(e) => setResponseNotes(e.target.value)} />
                </Field>
              </div>
            )}
            {detail.decisionNotes && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">Comentario de la empresa:</span> {detail.decisionNotes}
              </p>
            )}

            <ErrorText>{error}</ErrorText>

            <div className="flex flex-wrap gap-2">
              {canRespond && ['REQUESTED', 'RESPONDED'].includes(detail.status) && (
                <Button
                  variant="cta"
                  disabled={!detail.company || !allPriced || respond.isPending}
                  onClick={() => respond.mutate()}
                  title={!detail.company ? 'Primero vincula la empresa' : undefined}
                >
                  {detail.status === 'RESPONDED' ? 'Actualizar respuesta' : 'Responder cotización'}
                </Button>
              )}
              {canRespond && detail.status === 'ACCEPTED' && !detail.agreementId && (
                <Button variant="cta" disabled={convert.isPending} onClick={() => convert.mutate()}>
                  Convertir en convenio
                </Button>
              )}
              {detail.agreementId && <Badge className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800"><Icon name="check" className="h-3.5 w-3.5" />Convenio generado</Badge>}
              {canRespond && ['REQUESTED', 'RESPONDED'].includes(detail.status) && (
                <Button variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                  Descartar cotización
                </Button>
              )}
            </div>
            {detail.status === 'RESPONDED' && (
              <p className="text-xs text-gray-500">
                La empresa recibió el aviso por correo y puede aceptar o rechazar desde su portal.
              </p>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}
